import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { fetchServices, addUpdateService, deleteService } from '../../database/databaseServices';
import { fetchStaffProfiles, updateMyProfile } from '../../database/databaseProfiles';

const emptyProfileForm = { specialty: '', bio: '', avatar_url: '', phone: '', email: '' };

export default function AdminServices() {
  const [currentUserId, setCurrentUserId] = useState(null);
  const [role, setRole] = useState(null);
  const [services, setServices] = useState([]);
  const [staffProfiles, setStaffProfiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    staff_id: '',
    title: '',
    price: '',
    duration: '',
  });

  const [profileForm, setProfileForm] = useState(emptyProfileForm);
  const [profileName, setProfileName] = useState('');
  const [profileFieldsSupported, setProfileFieldsSupported] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);

  const isAdmin = role === 'admin';

  const loadServices = async (staffId) => {
    const data = await fetchServices(isAdmin ? null : staffId);
    setServices(data || []);
  };

  const loadMyProfile = async (userId) => {
    let { data, error } = await supabase
      .from('profiles')
      .select('full_name, phone, email, bio, avatar_url, specialty')
      .eq('id', userId)
      .single();

    if (error) {
      setProfileFieldsSupported(false);
      ({ data } = await supabase.from('profiles').select('full_name, phone, email').eq('id', userId).single());
    }

    if (data) {
      setProfileName(data.full_name || '');
      setProfileForm({
        specialty: data.specialty || '',
        bio: data.bio || '',
        avatar_url: data.avatar_url || '',
        phone: data.phone || '',
        email: data.email || '',
      });
    }
  };

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      const user = u?.user ?? null;
      setCurrentUserId(user?.id ?? null);

      if (!user) return;

      const { data: prof } = await supabase.from('profiles').select('type').eq('id', user.id).single();
      const userRole = prof?.type ?? null;
      setRole(userRole);

      if (userRole === 'admin') {
        const profiles = await fetchStaffProfiles();
        setStaffProfiles(profiles || []);
      }

      await loadMyProfile(user.id);
      await loadServices(user.id);
    })();
  }, []);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { specialty, bio, avatar_url, phone, email } = profileForm;
      const payload = profileFieldsSupported ? { specialty, bio, avatar_url, phone, email } : { phone, email };
      await updateMyProfile(currentUserId, payload);
      alert('Perfil guardado.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUserId) return;

    const filePath = `avatar-${currentUserId}`;
    const { error: uploadError } = await supabase.storage.from('class-images').upload(filePath, file, {
      upsert: true,
      cacheControl: '3600',
      contentType: file.type,
    });

    if (uploadError) {
      alert(`No se pudo subir la foto: ${uploadError.message}`);
      return;
    }

    const { data: publicData } = supabase.storage.from('class-images').getPublicUrl(filePath);
    const publicUrl = `${publicData.publicUrl}?t=${Date.now()}`;

    if (profileFieldsSupported) {
      await updateMyProfile(currentUserId, { avatar_url: publicUrl });
    }
    setProfileForm((prev) => ({ ...prev, avatar_url: publicUrl }));
  };

  const openAddModal = () => {
    setEditingId(null);
    setFormData({ staff_id: isAdmin ? '' : currentUserId, title: '', price: '', duration: '' });
    setShowModal(true);
  };

  const openEditModal = (service) => {
    setEditingId(service.id);
    setFormData({
      staff_id: service.staff_id,
      title: service.title,
      price: service.price ?? '',
      duration: service.duration || '',
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const { staff_id, title, price, duration } = formData;

    if (isAdmin && !staff_id) {
      alert('Selecciona un profesional para este servicio.');
      return;
    }

    const payload = {
      staff_id: isAdmin ? staff_id : currentUserId,
      title,
      price: price === '' ? null : Number(price),
      duration,
    };

    await addUpdateService(payload, editingId);
    await loadServices(currentUserId);
    setShowModal(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este servicio definitivamente?')) return;
    await deleteService(id);
    await loadServices(currentUserId);
  };

  return (
    <section className="w-[95%] md:w-[90%] lg:w-[80%] mx-auto py-6">
      {/* Mi perfil */}
      <div className="bg-card border border-sand rounded-lg p-6 mb-8">
        <h3 className="text-lg font-serif text-terracotta mb-1">Mi perfil</h3>
        <p className="text-sm text-ink/60 mb-4">Esto es lo que van a ver los clientes en la sección de Profesionales de la página de inicio.</p>

        {!profileFieldsSupported && (
          <p className="mb-4 text-sm bg-yellow-50 border border-yellow-400 text-yellow-800 rounded px-3 py-2">
            Todavía falta correr la migración que agrega los campos de foto, bio y especialidad. Avisale a quien administre la base.
          </p>
        )}

        <form onSubmit={handleSaveProfile} className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6">
          <div className="flex flex-col items-center gap-2">
            {profileForm.avatar_url ? (
              <img src={profileForm.avatar_url} alt={profileName} className="w-28 h-28 rounded-md object-cover" />
            ) : (
              <div className="w-28 h-28 rounded-md bg-sand flex items-center justify-center text-terracotta-dark font-serif text-3xl">
                {(profileName || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <label className="text-xs text-terracotta cursor-pointer hover:underline">
              Cambiar foto
              <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" disabled={!profileFieldsSupported} />
            </label>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium">Nombre</span>
              <input type="text" value={profileName} disabled className="w-full border border-sand bg-sand/30 text-ink/70 px-3 py-2 rounded" />
            </label>

            <label className="block">
              <span className="text-sm font-medium">Especialidad</span>
              <input
                type="text"
                placeholder="Ej: Masajes, Acupuntura y terapias"
                value={profileForm.specialty}
                onChange={(e) => setProfileForm((f) => ({ ...f, specialty: e.target.value }))}
                disabled={!profileFieldsSupported}
                className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded disabled:opacity-50"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium">Bio</span>
              <textarea
                placeholder="Una descripción corta que vean tus clientes"
                value={profileForm.bio}
                onChange={(e) => setProfileForm((f) => ({ ...f, bio: e.target.value }))}
                disabled={!profileFieldsSupported}
                className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded min-h-[80px] disabled:opacity-50"
              />
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm font-medium">Teléfono (WhatsApp)</span>
                <input
                  type="tel"
                  placeholder="34600000000"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm((f) => ({ ...f, phone: e.target.value }))}
                  className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium">Email</span>
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={savingProfile}
              className="bg-terracotta text-cream px-4 py-2 rounded hover:bg-terracotta-dark transition disabled:opacity-50"
            >
              {savingProfile ? 'Guardando…' : 'Guardar perfil'}
            </button>
          </div>
        </form>
      </div>

      <div className="flex flex-wrap gap-3 items-center justify-end mb-4">
        <button onClick={openAddModal} className="bg-terracotta text-cream px-4 py-2 rounded hover:bg-terracotta-dark transition w-full sm:w-auto">
          Nuevo Servicio
        </button>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded border border-sand">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-sand text-left text-terracotta-dark">
              <th className="px-[2.5%] py-3 border border-sand">Servicio</th>
              {isAdmin && <th className="px-[2.5%] py-3 border border-sand">Profesional</th>}
              <th className="px-[2.5%] py-3 border border-sand">Duración</th>
              <th className="px-[2.5%] py-3 border border-sand">Precio</th>
              <th className="px-[2.5%] py-3 text-center border border-sand">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.id} className="bg-card hover:bg-sand/40">
                <td className="px-[2.5%] py-3 border border-sand align-top">{s.title}</td>
                {isAdmin && <td className="px-[2.5%] py-3 border border-sand align-top">{s.profiles?.full_name ?? '—'}</td>}
                <td className="px-[2.5%] py-3 border border-sand align-top">{s.duration || '—'}</td>
                <td className="px-[2.5%] py-3 border border-sand align-top">{s.price != null ? `€${s.price}` : '—'}</td>
                <td className="px-[2.5%] py-3 border border-sand text-center">
                  <div className="flex items-center justify-center gap-3">
                    <button onClick={() => openEditModal(s)} className="text-blue-600 hover:underline">
                      Editar
                    </button>
                    <button onClick={() => handleDelete(s.id)} className="text-red-600 hover:underline">
                      Borrar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {services.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 5 : 4} className="text-center py-6 text-ink/60">
                  No hay servicios cargados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {services.length === 0 && <div className="text-center py-4 border border-sand rounded text-ink/60">No hay servicios cargados.</div>}

        {services.map((s) => (
          <div key={s.id} className="border border-sand rounded-lg p-4 shadow-sm bg-card">
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <span className="w-[35%] text-sm font-medium text-ink/70">Servicio</span>
                <span className="w-[65%] break-words">{s.title}</span>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <span className="w-[35%] text-sm font-medium text-ink/70">Profesional</span>
                  <span className="w-[65%]">{s.profiles?.full_name ?? '—'}</span>
                </div>
              )}
              <div className="flex gap-2">
                <span className="w-[35%] text-sm font-medium text-ink/70">Duración</span>
                <span className="w-[65%]">{s.duration || '—'}</span>
              </div>
              <div className="flex gap-2">
                <span className="w-[35%] text-sm font-medium text-ink/70">Precio</span>
                <span className="w-[65%]">{s.price != null ? `€${s.price}` : '—'}</span>
              </div>
            </div>

            <div className="mt-4 flex justify-between">
              <button onClick={() => openEditModal(s)} className="px-3 py-2 rounded border border-sand w-[48%] text-blue-700">
                Editar
              </button>
              <button onClick={() => handleDelete(s.id)} className="px-3 py-2 rounded border border-red-200 w-[48%] text-red-700">
                Borrar
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-card p-6 rounded-lg shadow-lg w-[96%] sm:w-[85%] md:w-[70%] lg:w-[50%] xl:w-[40%] max-w-[900px] max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg sm:text-xl font-serif text-terracotta mb-4">{editingId ? 'Editar servicio' : 'Nuevo servicio'}</h3>

            <form onSubmit={handleSave} className="space-y-4">
              {isAdmin && (
                <label className="block">
                  <span className="text-sm font-medium">Profesional</span>
                  <select
                    value={formData.staff_id}
                    onChange={(e) => setFormData((f) => ({ ...f, staff_id: e.target.value }))}
                    className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                    required
                  >
                    <option value="">Selecciona un profesional</option>
                    {staffProfiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.full_name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="block">
                <span className="text-sm font-medium">Título</span>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))}
                  className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                  required
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm font-medium">Duración</span>
                  <input
                    type="text"
                    placeholder="60 min"
                    value={formData.duration}
                    onChange={(e) => setFormData((f) => ({ ...f, duration: e.target.value }))}
                    className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-medium">Precio (€)</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData((f) => ({ ...f, price: e.target.value }))}
                    className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded border border-sand text-ink hover:bg-sand/40 transition w-[45%] sm:w-auto">
                  Cancelar
                </button>
                <button type="submit" className="bg-terracotta text-cream px-4 py-2 rounded hover:bg-terracotta-dark transition w-[45%] sm:w-auto">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
